import { Injectable } from '@angular/core';
import { LocalStoreManager } from './local-store-manager.service';
import { Utilities } from './utilities';
import { LocalStorageKeys } from './local-storage-keys';
import { DocumentationInput, SourceApp } from '@icadsistemi/ngx-icadsistemi-documentation-inline';
import { environment } from 'src/environments/environment';
import { OperatingModes, PriceLevels } from '../models/companies/companies.model';

@Injectable()
export class ConfigurationService {
    public appVersion: string = "4.0.0";

    public static readonly documentationBaseApiGwUrl: string = environment.documentationBaseUrl;
    public documentationInput: DocumentationInput;
    public static readonly cognitoLSKey = 'CognitoIdentityServiceProvider.' + environment.cognito.userPoolClientId;

    //#region Urls
    public baseUrl: string = Utilities.baseUrl().replace(/\/$/, '');
    public fallbackBaseUrl: string = null;
    public loginUrl: string = '/login';
    public dashboardUrl: string = '/dashboard';
    //#endregion

    private _sidebarBackgroungColor: string = null;
    private _sidebarActiveColor: string = null;

    private _localVersion: number = null;
    private _localSupplierId: number = null;
    private _localFuelstationId: number = null;
    private _hideFeatures: boolean = null;
    private _priceLevels: PriceLevels[];
    private _operatingModesCache: OperatingModes[];

    constructor(private localStorage: LocalStoreManager) {
        this.loadLocalChanges();
        this.documentationInput = new DocumentationInput(SourceApp.FASTLANE, true, true);
    }

    /**
     * Delete data related to the key from the local storage
     * @param key Key to delete.
     */
    public deleteLocalStorageData(key: string) {
        if (this.localStorage.exists(key)) this.localStorage.deleteData(key);
    }

    /**
     * Load data from the loacl storage.
     */
    private loadLocalChanges() {
        if (this.localStorage.exists(LocalStorageKeys.SIDEBAR_BACKGROUND_COLOR)) this._sidebarBackgroungColor = this.localStorage.getDataObject<string>(LocalStorageKeys.SIDEBAR_BACKGROUND_COLOR);
        if (this.localStorage.exists(LocalStorageKeys.SIDEBAR_ACTIVE_COLOR)) this._sidebarActiveColor = this.localStorage.getDataObject<string>(LocalStorageKeys.SIDEBAR_ACTIVE_COLOR);
    }

    /**
     * Save data on loacl storage
     * @param data Data
     * @param key Key
     */
    private saveToLocalStore(data: any, key: string) {
        setTimeout(() => this.localStorage.savePermanentData(data, key));
    }

    /**
     * Clear all data on local storage
     */
    public clearLocalChanges() {
        this._sidebarBackgroungColor = null;
        this.localStorage.deleteData(LocalStorageKeys.SIDEBAR_BACKGROUND_COLOR);
    }

    // Sidebar backgroung color
    set sidebarBackgroungColor(value: string) {
        this._sidebarBackgroungColor = value;
        this.saveToLocalStore(value, LocalStorageKeys.SIDEBAR_BACKGROUND_COLOR);
    }
    get sidebarBackgroungColor() {
        return this._sidebarBackgroungColor;
    }

    // Sidebar active color
    set sidebarActiveColor(value: string) {
        this._sidebarActiveColor = value;
        this.saveToLocalStore(value, LocalStorageKeys.SIDEBAR_ACTIVE_COLOR);
    }
    get sidebarActiveColor() {
        return this._sidebarActiveColor;
    }

    // Features notifications
    set hideFeatures(value: boolean) {
        this._hideFeatures = value;
        this.saveToLocalStore(value, LocalStorageKeys.HIDE_FEATURES);
    }
    get hideFeatures() {
        if (this._hideFeatures != null) return this._hideFeatures;
        return false;
    }

    // Local application version
    set localVersion(value: number) {
        this._localVersion = value;
        localStorage.setItem(LocalStorageKeys.LOCAL_VERSION, value.toString());
    }

    get localVersion() {
        this._localVersion = Number(localStorage.getItem(LocalStorageKeys.LOCAL_VERSION));
        if (this._localVersion != null) return this._localVersion;
        return 0;
    }

    // Local supplierid
    set localSupplierId(value: number) {
        this._localSupplierId = value;
    }
    get localSupplierId() {
        if (this._localSupplierId != null) return this._localSupplierId;
        return null;
    }

    // Local fuelstationId
    set localFuelstationId(value: number) {
        this._localFuelstationId = value;
    }
    get localFuelstationId() {
        if (this._localFuelstationId != null) return this._localFuelstationId;
        return null;
    }

    // Price levels cache
    set priceLevelsCache(value: PriceLevels[]) {
        this._priceLevels = value;
    }
    get priceLevelsCache() {
        return this._priceLevels;
    }

    // Operating modes cache
    set operatingModesCache(value: OperatingModes[]) {
        this._operatingModesCache = value;
    }
    get operatingModesCache() {
        return this._operatingModesCache;
    }

    /**
     * Method able to set the inline documentation parameters. When called, it configure the inline doc panel in the source component
     * @param pageId : represent the page identifier, should be unique in order to retrieve the written documentation
     * @param sectionId :
     * @param showPanel : if true, the inline doc panel will be visible, otherwise it will be not visible
     */
    public setInlineDocumentationParams(pageId: string, sectionId: string, showPanel: boolean) {
        this.documentationInput.pageId = pageId;
        this.documentationInput.sectionId = sectionId;
        this.documentationInput.showPanel = showPanel;
    }
}
